import { SKELETON_FILE_DIRECTIVE } from '@agilelab/plugin-wb-catalog-extension-common';
import {
  customAlertApiRef,
  MonacoEditor,
  WbBreadcrumb,
  WbBreadcrumbs,
  WbCard,
  WbCardContent,
  WbEditor,
  WbHeader,
} from '@agilelab/plugin-wb-platform';
import { Content } from '@mainstage/core-components';
import { useApi, useRouteRef } from '@mainstage/core-plugin-api';
import { Button, Grid, Typography } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import yaml from 'yaml';
import { panelCatalogApiRef } from '../../../api';
import { templateIndexRouteRef } from '../../../routes';
import { WbPage } from '@agilelab/plugin-wb-platform';

const DEFAULT_CATALOG_INFO = `${SKELETON_FILE_DIRECTIVE}\nkind: Component\r\nspec:\r\n  mesh:\r\n    name: \${{ parameters.name }}`;
const DEFAULT_PARAMETERS = 'parameters:\n  name: Test Component';

function useSkeletonRender() {
  const [rendered, setRendered] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const panelCatalogApi = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);

  const render = useCallback(
    async (catalogInfo: string, parameters: string) => {
      setLoading(true);
      setError(undefined);
      try {
        const params = parameters ? yaml.parse(parameters) : {};
        const res = await panelCatalogApi.renderSkeletonEntity({
          catalogInfo: catalogInfo.startsWith(SKELETON_FILE_DIRECTIVE)
            ? catalogInfo
            : `${SKELETON_FILE_DIRECTIVE}\n${catalogInfo}`,
          parameters: params,
        });
        setRendered(yaml.stringify(res.catalogInfo));
      } catch (err: any) {
        setError(err?.message ?? 'Rendering failed');
        alertApi.post({ error: err, severity: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [panelCatalogApi, alertApi],
  );

  return { rendered, loading, error, render };
}

export interface SkeletonRenderingPreviewResultProps {
  catalogInfo: string;
  parameters: string;
  height?: string;
}

/** Renders automatically on mount; shows only the rendered output — no inputs or button. */
export function SkeletonRenderingPreviewResult({
  catalogInfo,
  parameters,
  height = '100%',
}: SkeletonRenderingPreviewResultProps) {
  const { rendered, loading, error, render } = useSkeletonRender();

  useEffect(() => {
    render(catalogInfo, parameters);
  }, [catalogInfo, parameters, render]);

  let editorValue = rendered;
  if (loading) editorValue = 'Rendering...';
  else if (error) editorValue = `# Error: ${error}`;

  return (
    <WbEditor
      theme="white"
      height={height}
      language="yaml"
      value={editorValue}
      options={{ readOnly: true, minimap: { enabled: false } }}
    />
  );
}

export interface SkeletonRenderingPreviewContentProps {
  initialCatalogInfo?: string;
  initialParameters?: string;
  editorHeight?: string;
}

export function SkeletonRenderingPreviewContent({
  initialCatalogInfo,
  initialParameters,
  editorHeight = '400px',
}: SkeletonRenderingPreviewContentProps) {
  const [catalogInfo, setCatalogInfo] = useState(
    initialCatalogInfo ?? DEFAULT_CATALOG_INFO,
  );
  const [parameters, setParameters] = useState(
    initialParameters ?? DEFAULT_PARAMETERS,
  );
  const [_, setMonacoEditor] = useState<MonacoEditor>();
  const { rendered, loading, render } = useSkeletonRender();
  const renderedOutputRef = useRef<HTMLDivElement | null>(null);

  const handleRender = useCallback(async () => {
    await render(catalogInfo, parameters);
    renderedOutputRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [render, catalogInfo, parameters]);

  const editorOptions = useMemo(
    () => ({
      selectOnLineNumbers: true,
      contextmenu: false,
      readOnly: loading,
      diffCodeLens: false,
      originalEditable: false,
      glyphMargin: true,
      linkedEditing: true,
      renderMarginRevertIcon: true,
      minimap: { enabled: true },
    }),
    [loading],
  );

  return (
    <WbCard title="Renderer">
      <WbCardContent>
        <Grid container spacing={2} direction="column">
          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <Typography
                variant="body1"
                style={{ fontWeight: 'bold', padding: '10px' }}
              >
                Skeleton catalog info
              </Typography>
              <WbEditor
                theme="white"
                height={editorHeight}
                language="plaintext"
                value={catalogInfo}
                onChange={value => setCatalogInfo(value || '')}
                options={editorOptions}
                onMount={editor => setMonacoEditor(editor)}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="body1"
                style={{ fontWeight: 'bold', padding: '10px' }}
              >
                Parameters
              </Typography>
              <WbEditor
                theme="white"
                height={editorHeight}
                language="yaml"
                value={parameters}
                onChange={value => setParameters(value || '')}
                options={editorOptions}
                onMount={editor => setMonacoEditor(editor)}
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            justifyContent="center"
            style={{ padding: '10px' }}
          >
            <Button
              color="primary"
              size="large"
              variant="contained"
              disabled={loading}
              onClick={handleRender}
            >
              Render
            </Button>
          </Grid>
          <Grid item ref={renderedOutputRef}>
            <Typography
              variant="body1"
              style={{ fontWeight: 'bold', padding: '10px' }}
            >
              Rendered catalog info
            </Typography>
            <WbEditor
              theme="white"
              height={editorHeight}
              language="yaml"
              value={loading ? 'Rendering...' : rendered}
              options={{ ...editorOptions, readOnly: false }}
              onMount={editor => setMonacoEditor(editor)}
            />
          </Grid>
        </Grid>
      </WbCardContent>
    </WbCard>
  );
}

export function SkeletonRenderingPreviewPage() {
  const templateIndexLink = useRouteRef(templateIndexRouteRef);

  return (
    <WbPage themeId="home">
      <WbHeader
        title="Catalog Info Rendering Preview"
        breadcrumbs={
          <WbBreadcrumbs aria-label="breadcrumb">
            <WbBreadcrumb>Administration</WbBreadcrumb>
            <WbBreadcrumb to={templateIndexLink()}>Templates</WbBreadcrumb>
            <WbBreadcrumb>Catalog Info Rendering Preview</WbBreadcrumb>
          </WbBreadcrumbs>
        }
      />
      <Content>
        <SkeletonRenderingPreviewContent />
      </Content>
    </WbPage>
  );
}
